import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  PawPrint, Phone, Star, ArrowRight, Stethoscope, Scissors,
  Home as HomeIcon, ShoppingBag, Calendar, MessageCircle, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SERVICES, REVIEWS, FAQS, IMAGES, CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg, cdnSrcSet } from '../utils/cdn';
import ReviewsCarousel from '../components/ReviewsCarousel';
import FAQAccordion from '../components/FAQAccordion';
import WhyChooseUs from '../components/WhyChooseUs';
import StatsSection from '../components/StatsSection';
import ServiceArea from '../components/ServiceArea';
import CTABanner from '../components/CTABanner';
import WhyWeStarted from '../components/WhyWeStarted';
import InstagramSection from '../components/InstagramSection';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const TRUST_BADGES_HOME = [
  'Honest Recommendations',
  'Transparent Pricing',
  'Gentle Handling',
  'In-House Diagnostics',
  'Experienced Veterinary Team',
  'WhatsApp Follow-ups',
];

const Home = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
  return (
    <div>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* HERO SECTION */}
      <section className="relative min-h-[600px] sm:min-h-[calc(100vh-88px)] -mt-[88px] pt-[88px] overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            {/* Mobile portrait — tall 3:4 crop, center align (mural face stays in frame) */}
            <source
              media="(max-width: 639px)"
              srcSet={`${cdnImg(IMAGES.hero, { w: 720, h: 960, fit: 'cover', a: 'center' })} 1x, ${cdnImg(IMAGES.hero, { w: 1080, h: 1440, fit: 'cover', a: 'center' })} 2x`}
            />
            {/* Tablet — 4:3 crop, center align */}
            <source
              media="(max-width: 1023px)"
              srcSet={`${cdnImg(IMAGES.hero, { w: 1280, h: 960, fit: 'cover', a: 'center' })} 1x, ${cdnImg(IMAGES.hero, { w: 2048, h: 1536, fit: 'cover', a: 'center' })} 2x`}
            />
            {/* Desktop — 16:9 with weserv's attention auto-focus on the mural subject */}
            <source
              media="(min-width: 1024px)"
              srcSet={`${cdnImg(IMAGES.hero, { w: 1920, h: 1080, fit: 'cover', a: 'attention' })} 1x, ${cdnImg(IMAGES.hero, { w: 2560, h: 1440, fit: 'cover', a: 'attention' })} 2x`}
            />
            <img
              src={cdnImg(IMAGES.hero, { w: 1920, h: 1080, fit: 'cover', a: 'attention' })}
              alt="Ichi and Ori Pet Care waiting area"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30 sm:from-black/80 sm:via-black/55 sm:to-black/20" />
        </div>

        <div className="relative max-w-[1280px] mx-auto px-5 lg:px-10 min-h-[600px] sm:min-h-[calc(100vh-88px)] flex items-center">
          <div className="max-w-2xl lg:max-w-3xl text-white">
            <div className="inline-flex items-center gap-3 mb-6 animate-fade-in">
              <span className="w-10 h-10 rounded-full bg-[#e870b6] flex items-center justify-center">
                <PawPrint className="w-5 h-5 text-white" />
              </span>
              <span className="text-[14px] tracking-[0.3em] uppercase font-medium">Our passion is animals</span>
            </div>
            <h1 className="font-serif text-[42px] md:text-[60px] lg:text-[72px] leading-[1.05] font-extrabold mb-6 animate-slide-up">
              Thoughtful Veterinary Care for Every Stage of Your Pet&apos;s Life
            </h1>
            <p className="text-[18px] md:text-[20px] text-white/85 mb-10 max-w-xl animate-slide-up">
              Consultations, vaccinations, diagnostics, surgery and preventive healthcare for dogs, cats and rabbits, delivered with gentle handling in Sector 90, Noida.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4 animate-slide-up w-full max-w-sm sm:max-w-none">
              <Link to="/book" onClick={() => trackBookButtonClick('hero')} className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto rounded-full bg-[#f084c4] hover:bg-[#e870b6] text-white px-8 py-7 h-auto text-[15px] font-semibold gap-2 shadow-lg hover:shadow-2xl hover:scale-[1.02] sm:hover:scale-105 transition-all">
                  <Calendar className="w-4 h-4" /> Request Appointment
                </Button>
              </Link>
              <a
                href={`https://wa.me/${CONTACT.phoneRaw.replace('+', '')}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWhatsAppClick('hero')}
                className="w-full sm:w-auto"
              >
                <Button className="w-full sm:w-auto rounded-full bg-[#3ddc7e] hover:bg-[#25D366] text-white px-8 py-7 h-auto text-[15px] font-semibold gap-2 shadow-lg hover:shadow-2xl hover:scale-[1.02] sm:hover:scale-105 transition-all">
                  <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                </Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('hero')} className="w-full sm:w-auto sm:ml-1">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Us <span className="hidden sm:inline">{CONTACT.phone}</span>
                </span>
              </a>
            </div>
          </div>
        </div>

        <PawPrint className="absolute bottom-10 right-10 w-24 h-24 text-white/10 rotate-12 hidden md:block" />
      </section>

      {/* WHY WE STARTED — emotional teaser */}
      <WhyWeStarted />

      {/* OUR STORY — full narrative on Home (also on About) */}
      <section className="py-20 lg:py-28 bg-[#1a0b3d] text-white relative overflow-hidden">
        <PawPrint className="absolute top-10 right-10 w-32 h-32 text-white/5" />
        <PawPrint className="absolute bottom-20 left-10 w-24 h-24 text-white/5 rotate-45" />

        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-[#e870b6] font-semibold tracking-[0.2em] text-[13px] uppercase mb-4">
              <PawPrint size={16} /> Our Story
            </span>
            <h2 className="font-serif text-[40px] lg:text-[56px] font-extrabold leading-[1.1]">
              Our Story: A Legacy of Love and Care
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {/* Mobile: show only the two-dogs photo. Desktop: show both. */}
            <div className="hidden sm:block rounded-2xl overflow-hidden aspect-square shadow-2xl group bg-white/5">
              <img src={cdnImg(IMAGES.story1, { w: 800 })} alt="Ichi and Ori family memory 1" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-square shadow-2xl group bg-white/5">
              <img src={cdnImg(IMAGES.story3, { w: 800 })} alt="Ichi and Ori — the two dogs" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          </div>

          <div className="space-y-6 text-[16px] lg:text-[17px] leading-relaxed text-white/85">
            <p>
              At <span className="text-[#e870b6] font-semibold">Ichi and Ori Pet Care</span>, our journey is deeply personal, a story of love, loss, and an unwavering commitment to the well-being of pets. <span className="text-[#f3d6f5] italic">If you&apos;ve ever worried about your pet like family, you&apos;ll understand why we built Ichi &amp; Ori differently.</span>
            </p>
            <p>
              For over two decades, I have been a devoted pet parent, raising and loving my pets as my own children. Through this journey, I faced countless challenges&mdash;ones that every pet parent experiences. I learned, grew, and made mistakes along the way, always striving to give my pets the best life possible.
            </p>
            <p>
              Then, life took an unexpected turn. In the span of just six months, I lost three of the most precious beings in my life&mdash;my beloved father, my cockatoo <span className="text-[#f3d6f5] font-semibold">Zazu</span>, and my dog <span className="text-[#f3d6f5] font-semibold">Ichi</span>. Their loss left an unfillable void, and suddenly, I was left with only <span className="text-[#f3d6f5] font-semibold">Ori</span>, my last remaining baby.
            </p>
          </div>

          {/* Bottom image pair — hidden on mobile to reduce data weight */}
          <div className="hidden sm:grid sm:grid-cols-2 gap-6 my-12">
            {[IMAGES.story4, IMAGES.story2].map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-square shadow-2xl group bg-white/5">
                <img src={cdnImg(src, { w: 800 })} alt={`Ichi and Ori family memory ${i + 3}`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>

          <div className="space-y-6 text-[16px] lg:text-[17px] leading-relaxed text-white/85 sm:mt-0 mt-12">
            <p>
              Through my grief, I found purpose. My father, the ultimate animal lover, had always dreamed of a world where pets received the same love and care as family. Inspired by his devotion and my own experiences as a pet parent, I decided to turn my pain into something meaningful&mdash;to create a pet care experience that truly understands and supports both pets and their parents.
            </p>
            <p>
              Thus, <span className="text-[#e870b6] font-semibold">Ichi and Ori Pet Care</span> was born&mdash;a tribute to the ones I lost and a promise to every pet and pet parent who walks through our doors. Here, we don&rsquo;t just provide veterinary care, grooming, day boarding, and premium retail&mdash;we create a sanctuary where pets are cherished, and pet parents feel understood.
            </p>
            <p>
              This is more than a business. It is my heartfelt mission to ensure that no pet parent feels alone in their journey and that every pet receives the love, comfort, and care they deserve.
            </p>
            <p className="text-[#f3d6f5] font-serif text-[20px] lg:text-[22px] font-semibold pt-4 border-t border-white/15 mt-4">
              Welcome to Ichi and Ori Pet Care&mdash;where pets are family, and their happiness is our purpose.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <StatsSection />

      {/* WHY CHOOSE US */}
      <WhyChooseUs />

      {/* SERVICES */}
      <section id="services" className="py-20 lg:py-24 bg-[#faf5fb]">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-[#e870b6] font-semibold tracking-[0.2em] text-[13px] uppercase mb-3">
              <PawPrint size={16} /> Our Services
            </span>
            <h2 className="font-serif text-[42px] lg:text-[56px] font-extrabold text-[#1a0b3d]">
              What We Offer
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {SERVICES.map((service, idx) => (
              <ServiceCard key={service.id} service={service} idx={idx} />
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-5 mt-14 bg-white rounded-2xl shadow-md py-6 px-8 max-w-2xl mx-auto">
            <Link to="/book" onClick={() => trackBookButtonClick('services_section')}>
              <Button className="rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white px-7 py-6 h-auto gap-2">
                <Calendar size={16} /> Enquire Now
              </Button>
            </Link>
            <p className="text-slate-600 text-[15px]">
              Call us <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('services_section')} className="text-[#5b3eb5] font-bold border-b border-dashed border-[#5b3eb5] hover:text-[#e870b6]">{CONTACT.phone}</a> for detailed information!
            </p>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <ServiceArea />

      {/* REVIEWS */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-[#e870b6] font-semibold tracking-[0.2em] text-[13px] uppercase mb-3">
              <PawPrint size={16} /> Happy Pet Lovers
            </span>
            <h2 className="font-serif text-[42px] lg:text-[56px] font-extrabold text-[#1a0b3d] mb-4">
              What Pet Parents Say
            </h2>
            <p className="text-slate-600 text-[16px] mb-6">Trusted by hundreds of pet parents across Noida.</p>

            {/* Google rating badge */}
            <div className="inline-flex items-center gap-4 bg-white border border-[#f3d6f5] rounded-2xl shadow-md px-6 py-4">
              <svg width="32" height="32" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="text-[26px] font-extrabold text-[#1a0b3d] leading-none">{GOOGLE_RATING.rating.toFixed(1)}</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
                <div className="text-[12px] text-slate-500 uppercase tracking-wider font-medium mt-0.5">
                  Rated by {GOOGLE_RATING.count}+ pet parents on Google
                </div>
              </div>
              <a
                href={CONTACT.googleReviews}
                target="_blank"
                rel="noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 text-[#5b3eb5] hover:text-[#e870b6] font-semibold text-[14px] ml-2 border-l border-slate-200 pl-5"
              >
                View on Google <ArrowRight size={14} />
              </a>
            </div>
          </div>

          <ReviewsCarousel reviews={REVIEWS} />

          <div className="text-center mt-10">
            <a href={CONTACT.googleReviews} target="_blank" rel="noreferrer">
              <Button className="rounded-full bg-white border border-[#5b3eb5] text-[#5b3eb5] hover:bg-[#5b3eb5] hover:text-white px-8 py-6 h-auto gap-2 shadow-sm hover:shadow-lg transition-all">
                View All Reviews on Google <ArrowRight size={16} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <InstagramSection />

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-[#faf5fb]">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_1.4fr] gap-14">
          <div>
            <span className="inline-flex items-center gap-2 text-[#e870b6] font-semibold tracking-[0.2em] text-[13px] uppercase mb-3">
              <PawPrint size={16} /> Got Questions?
            </span>
            <h2 className="font-serif text-[40px] lg:text-[52px] font-extrabold text-[#1a0b3d] leading-[1.1] mb-8">
              Frequently asked questions
            </h2>
            <Link to="/contact">
              <Button className="rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white px-7 py-6 h-auto gap-2">
                <MessageCircle size={16} /> Ask a Question
              </Button>
            </Link>
          </div>
          <FAQAccordion items={FAQS} />
        </div>
      </section>

      {/* FINAL CTA */}
      <CTABanner />

      {/* TRUST BADGES — just before the site footer */}
      <section className="bg-white border-t border-[#f3d6f5]/60 py-16 lg:py-20">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">TRUST, IN EVERY VISIT</span>
            <h2 className="font-serif text-[28px] sm:text-[34px] lg:text-[40px] font-extrabold text-[#1a0b3d] leading-[1.1]">
              Why Pet Parents Choose Ichi &amp; Ori
            </h2>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5 max-w-3xl mx-auto">
            {TRUST_BADGES_HOME.map((b, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#e870b6]" strokeWidth={3} />
                </span>
                <span className="text-[#1a0b3d] text-[15px] sm:text-base font-semibold">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

const iconFor = (id) => {
  switch (id) {
    case 'boarding': return HomeIcon;
    case 'veterinary': return Stethoscope;
    case 'grooming': return Scissors;
    default: return ShoppingBag;
  }
};

const ServiceCard = ({ service, idx }) => {
  const Icon = iconFor(service.id);
  return (
    <Link
      to={service.link}
      className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl border border-transparent hover:border-[#f3d6f5] transition-all duration-300 hover:-translate-y-2 relative overflow-hidden"
      style={{ animationDelay: `${idx * 80}ms` }}
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#f3d6f5]/60 group-hover:bg-[#e870b6]/20 transition-colors" />
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-[#f3d6f5] flex items-center justify-center mb-6 group-hover:bg-[#5b3eb5] transition-colors">
          <Icon className="w-8 h-8 text-[#5b3eb5] group-hover:text-white transition-colors" />
        </div>
        <h3 className="font-serif text-[26px] font-bold text-[#1a0b3d] mb-3">{service.title}</h3>
        <p className="text-slate-600 text-[15px] leading-relaxed mb-5">{service.description}</p>
        <span className="inline-flex items-center gap-2 text-[#5b3eb5] font-semibold text-[14px] group-hover:gap-3 transition-all">
          Learn more <ArrowRight size={16} />
        </span>
      </div>
    </Link>
  );
};

export default Home;
